import { baseApi } from '../../../shared/api/baseApi';
import type { SupportRequestDto, SupportRequestsPageDto } from '../model/dto';
import { toSupportRequest, toSupportRequestPatchDto, type SupportRequestPatch } from '../model/mappers';
import type { SupportRequest } from '../model/types';

const ENDPOINT = '/api/admin/tech_supports/technical_support';

const TAG = 'SupportRequest' as const;

const FETCH_ALL_SIZE = 1000;

export interface SupportRequestsPageArgs {
    pageNum: number;
    pageSize: number;
    query?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface SupportRequestsPage {
    items: SupportRequest[];
    total: number;
    pages: number;
    pageNum: number;
}

export const supportRequestApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getSupportRequests: build.query<SupportRequest[], void>({
            query: () => ({
                url: ENDPOINT,
                params: { page_size: FETCH_ALL_SIZE, page_num: 1, query: '' },
            }),
            transformResponse: (response: SupportRequestsPageDto): SupportRequest[] =>
                Array.isArray(response?.data) ? response.data.map(toSupportRequest) : [],
            providesTags: [{ type: TAG, id: 'LIST' }],
        }),

        /** Одна страница: пагинация, поиск и сортировка на стороне сервера. */
        getSupportRequestsPage: build.query<SupportRequestsPage, SupportRequestsPageArgs>({
            query: ({ pageNum, pageSize, query = '', sortBy, sortOrder }) => ({
                url: ENDPOINT,
                params: {
                    page_size: pageSize,
                    page_num: pageNum,
                    query,
                    ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder ?? 'desc' } : {}),
                },
            }),
            transformResponse: (response: SupportRequestsPageDto): SupportRequestsPage => ({
                items: Array.isArray(response?.data) ? response.data.map(toSupportRequest) : [],
                total: response?.count ?? 0,
                pages: response?.pages ?? 1,
                pageNum: response?.page_num ?? 1,
            }),
            providesTags: [{ type: TAG, id: 'LIST' }],
        }),

        /** Карточка обращения: у детальной ручки полнее description, answer и files. */
        getSupportRequest: build.query<SupportRequest, string>({
            query: (id) => `${ENDPOINT}/${id}`,
            transformResponse: (response: SupportRequestDto) => toSupportRequest(response),
            providesTags: (_result, _error, id) => [{ type: TAG, id }],
        }),

        updateSupportRequest: build.mutation<SupportRequest, { id: string; patch: SupportRequestPatch }>({
            query: ({ id, patch }) => ({
                url: `${ENDPOINT}/${id}`,
                method: 'PATCH',
                body: toSupportRequestPatchDto(patch),
            }),
            transformResponse: (response: SupportRequestDto) => toSupportRequest(response),
            invalidatesTags: (_result, _error, { id }) => [
                { type: TAG, id },
                { type: TAG, id: 'LIST' },
            ],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetSupportRequestsQuery,
    useGetSupportRequestsPageQuery,
    useGetSupportRequestQuery,
    useUpdateSupportRequestMutation,
} = supportRequestApi;